// Domain helpers for the 30 powers / 6 groups taxonomy - platform
// constants used by Save · Powers and Grow.
//
// `elements` is typed as `string[]` rather than `CreativityElement[]`
// because the codegen emits `CreativityElement` in both `types.ts` and
// (separately) `operations.ts`. Strict typecheck treats those as
// distinct nominal types even though their string-literal members are
// identical. Widening to `string` here avoids the cross-file type
// mismatch without any runtime change. If you need narrow Element
// typing in code, cast at the call site.

export type ElGroup =
  | "RESILIENCE"
  | "RESULT_ORIENTATION"
  | "FUTURE_ORIENTATION"
  | "INVENTIVENESS"
  | "TEAM_SPIRIT"
  | "COMMUNICATION";

// `labelId` reuses the shared cluster labels from the power library
// (`power.tag.*`) - resolve via intl.formatMessage. The six ElGroup keys
// map 1:1 to the six PowerTags.
//
// Cross-repo naming: these display labels intentionally diverge from genesis'
// enum identifiers (TEAM_SPIRIT -> "Teamability"; FAITH -> "Belief",
// SELF_AWARENESS -> "Self-Perception").
// Display wording is owned here in i18n, not in genesis.
// CAVEAT: group labels currently live in two places - power.tag.* (here) and
// match.group.* (Match UI via match-design-rules). Consolidating these into one
// source is pending team discussion.
export const ELEMENT_GROUPS: Record<
  ElGroup,
  { labelId: string; elements: string[] }
> = {
  RESILIENCE: {
    labelId: "power.tag.Re",
    elements: ["PERSEVERANCE", "MINDFULNESS", "ADAPTION", "CONCENTRATION", "AGGRESSION"],
  },
  RESULT_ORIENTATION: {
    labelId: "power.tag.Ro",
    elements: ["INTENTION", "DILIGENCE", "STRATEGY", "RESPONSIBILITY", "CHARISMA"],
  },
  FUTURE_ORIENTATION: {
    labelId: "power.tag.Fo",
    elements: ["COURAGE", "FAITH", "IMAGINATION", "ABSTRACTION", "PASSION"],
  },
  INVENTIVENESS: {
    labelId: "power.tag.In",
    elements: ["CURIOSITY", "INSPIRATION", "IMPROVISATION", "AUTONOMY", "INTUITION"],
  },
  TEAM_SPIRIT: {
    labelId: "power.tag.Ts",
    elements: ["TOLERANCE", "EMPATHY", "HUMOR", "FIDELITY", "FAIRNESS"],
  },
  COMMUNICATION: {
    labelId: "power.tag.Co",
    elements: ["SELF_AWARENESS", "PERCEPTION", "LANGUAGE", "EXPRESSION", "JOY"],
  },
};

export const ELEMENT_CODES: Record<string, string> = {
  PERSEVERANCE: "Psc", MINDFULNESS: "Min", ADAPTION: "Ada", CONCENTRATION: "Con", AGGRESSION: "Agg",
  INTENTION: "Int", DILIGENCE: "Dil", STRATEGY: "Str", RESPONSIBILITY: "Rpy", CHARISMA: "Cha",
  COURAGE: "Cou", FAITH: "Fai", CURIOSITY: "Cur", ABSTRACTION: "Abs", PASSION: "Pas",
  IMAGINATION: "Ima", INSPIRATION: "Ins", IMPROVISATION: "Imp", AUTONOMY: "Aut", INTUITION: "Inu",
  TOLERANCE: "Tol", EMPATHY: "Emp", HUMOR: "Hum", FIDELITY: "Fid", FAIRNESS: "Fns",
  SELF_AWARENESS: "Awe", PERCEPTION: "Per", LANGUAGE: "Lan", EXPRESSION: "Exp", JOY: "Joy",
};

export const GROUP_ORDER: ElGroup[] = [
  "RESILIENCE", "RESULT_ORIENTATION", "FUTURE_ORIENTATION",
  "INVENTIVENESS", "TEAM_SPIRIT", "COMMUNICATION",
];

// Power artwork lives in public/powers/<code>.jpg, where <code> is the
// lowercased 3-letter element code (ported from discovery's images/elements).
export function elementImageSrc(element: string): string | null {
  const code = ELEMENT_CODES[element];
  return code ? `/powers/${code.toLowerCase()}.jpg` : null;
}

export function elementDisplayName(element: string): string {
  return element.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

// i18n message id for a power's localized name (`power.name.<code>`). Resolve via
// intl.formatMessage with elementDisplayName(element) as the defaultMessage
// fallback.
export function elementNameId(element: string): string {
  return `power.name.${ELEMENT_CODES[element] ?? element}`;
}

export function findGroup(element: string): ElGroup | null {
  for (const [group, meta] of Object.entries(ELEMENT_GROUPS)) {
    if (meta.elements.includes(element)) return group as ElGroup;
  }
  return null;
}
